import {
    Component,
    Inject,
    LOCALE_ID,
    OnInit,
    OnDestroy,
    AfterViewInit,
    ViewChild,
    ElementRef,
    QueryList,
    ViewChildren
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ApplicationFacade } from '@ridango/components';
import { AppStartupService, BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import { MessagesService } from '../../services/messages.service';
import { fromEvent } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

@Component({
    templateUrl: './account-messages-page.component.html',
    styleUrls: ['./account-messages-page.component.scss']
})
export class AccountMessagesPageComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('containerElement', { static: true }) containerElement!: ElementRef;
    @ViewChildren('messageItem') messageItems!: QueryList<any>;

    private readonly DEFAULT_PAGE_SIZE = this.messagesService.DEFAULT_PAGE_SIZE;
    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    readonly DATE_TIME_FORMAT = this.env.PRIMARY_DATE_TIME_FORMAT;
    readonly TIMEZONE = this.appStartupService.getTimezone();
    messagesList: any = [];
    page = 0;
    loading = false;
    allMessagesLoaded = false;
    fromMessageDetailView = false;
    lastOpenedMessageId: number | null = null;

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private applicationFacade: ApplicationFacade,
        private translate: TranslateService,
        public appStartupService: AppStartupService,
        private messagesService: MessagesService,
        private router: ActivatedRoute
    ) {
        super();
    }

    ngOnInit() {
        this.locale = this.translate.currentLang;
        this.router.queryParams.subscribe((params: any) => {
            if (params?.fromMessageDetailView) {
                this.lastOpenedMessageId = params.id;
                this.fromMessageDetailView = true;
                const pageSize = this.messagesService.getPreviousPageSize();
                this.loadMessages(pageSize);
                return;
            }

            // restore previous state
            this.messagesService.page = this.page;
            this.loadMessages(this.DEFAULT_PAGE_SIZE);
        });
    }

    ngAfterViewInit() {
        this.messageItems.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.scrollToLastOpenedMessage();
        });
        fromEvent(this.containerElement.nativeElement, 'scroll')
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                const isScrollInBottom =
                    this.containerElement.nativeElement.scrollHeight -
                        this.containerElement.nativeElement.scrollTop -
                        this.containerElement.nativeElement.clientHeight <=
                    10;

                if (isScrollInBottom) {
                    this.fromMessageDetailView = false;
                    this.loadMessages(this.DEFAULT_PAGE_SIZE);
                }
            });
    }

    handleBackClicked(): void {
        window.history.back();
    }

    onMessageClick(): void {
        this.savePageParams();
    }

    private loadMessages(size: number): void {
        if (this.loading || this.allMessagesLoaded) return;

        this.loading = true;
        this.messagesService
            .fetchMessagesList(this.page, size)
            .pipe(
                tap(() => {
                    if (!this.fromMessageDetailView) return;

                    this.page = this.messagesService.page;
                })
            )
            .subscribe((response: { data: any[]; meta: any }) => {
                if (response.data.length > 0) {
                    this.messagesList = [...this.messagesList, ...response.data];
                    !this.fromMessageDetailView && this.page++;
                } else {
                    this.allMessagesLoaded = true;
                }
                this.loading = false;
            });
    }

    private savePageParams() {
        this.messagesService.page = this.page;
        this.messagesService.pageSize = this.DEFAULT_PAGE_SIZE;
    }

    private scrollToLastOpenedMessage(): void {
        if (!this.lastOpenedMessageId) return;

        const messageElement = this.messageItems.find(
            (el) => el.nativeElement?.id === `message-${this.lastOpenedMessageId}`
        );
        if (messageElement?.nativeElement) {
            messageElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.lastOpenedMessageId = null;
    }
}
